import { useEffect } from 'react'
import { trackInternalEvent } from '@rio/tracking'

import { Colors } from 'cio-design-library/design-tokens'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'

import { Skeleton, InfoBox, SuccessBox } from '../..'
import { WidgetTheme } from '../../WidgetTheme'
import { WidgetProps } from '../../widget.types'
import { WidgetBox, WidgetContainer } from '../../widget.styles'

import { useThemeConfig } from '../../hooks/useThemeConfig'
import { useResumeCraftingConfig } from './hooks/useResumeCraftingConfig'
import { useResumeCraftingActions } from './hooks/useResumeCraftinActions'

import { ActionButton } from './ActionButton'
import { IlustrationBox } from './IlustrationBox'

export const ResumeCrafting = ({ service }: WidgetProps) => {
  const resumeId = service.data?.calculated_item_id
  const { isPhone } = useMediaQueries()
  const themeState = useThemeConfig({ service })
  const { text, status, resume, suggestion, isLoading, resumePreview } = useResumeCraftingConfig({
    resumeId,
    service,
  })
  const { handleNoResumeClick, handleCompletedClick, handleInProgressClick } =
    useResumeCraftingActions(service)

  const theme = themeState[status]
  const score = resume?.resumeScore || 0
  const isInitial = status === 'initial'
  const isCompleted = status === 'completed'
  const isInProgress = status === 'progress'
  const widgetText = {
    badge: text.badge || '',
    header: text.header,
    subHeader: text.subHeader,
  }

  const getColorsByScore = (score: number) => {
    const isGood = score > 70
    const isAverage = score > 30

    return {
      background: isGood ? Colors.Green10 : isAverage ? Colors.Amber10 : Colors.Red10,
      percentage: isGood ? Colors.Green40 : isAverage ? Colors.Amber40 : Colors.Red40,
    }
  }

  const getBackground = () => {
    if (isPhone) {
      return `linear-gradient(180deg, ${Colors.Blue10} 20.89%, ${
        getColorsByScore(score).background
      } 148.96%);`
    }

    return `linear-gradient(98deg, 
      ${Colors.Blue10} 30%, 
      ${getColorsByScore(score).background} 100%), 
      ${Colors.Blue10};
    `
  }

  const overrideTheme = {
    ...theme,
    background: getBackground(),
  }

  const handleClick = () => {
    if (!isInProgress) {
      trackInternalEvent('progress_cta_click', {
        score: service.title,
        cta: text.actionText,
      })
    }

    if (isInitial) return handleNoResumeClick()
    if (isCompleted) return handleCompletedClick()
    if (isInProgress) {
      trackInternalEvent('progress_cta_click', {
        score: service.title,
        cta: suggestion?.text,
      })
      handleInProgressClick(resume?.id, suggestion?.key)
    }
  }

  useEffect(() => {
    if (isCompleted) {
      trackInternalEvent('progress_score_completed', { score: service.title })
    }
  }, [isCompleted, service.title])

  if (isLoading) {
    return <Skeleton service={service} tipOrder={service.widgetTipOrder} />
  }

  return (
    <WidgetTheme
      theme={isInProgress ? overrideTheme : theme}
      tipOrder={service.widgetTipOrder}
      tipBackgroundColor={theme.background}
    >
      <WidgetContainer>
        <WidgetBox>
          <InfoBox
            isCompleted={isCompleted}
            text={widgetText}
            data-testid={`resume-crafting-info-${status}`}
          >
            <ActionButton
              status={status}
              actionText={text.actionText}
              suggestion={suggestion}
              onClick={handleClick}
            />
          </InfoBox>
        </WidgetBox>
        <WidgetBox>
          {isCompleted ? (
            <SuccessBox />
          ) : (
            <IlustrationBox
              data-testid={`resume-crafting-ilustration-box-${status}`}
              resumePreview={resumePreview}
              score={score}
              background={getColorsByScore(score).percentage}
              scoreText={text.scoreText}
            />
          )}
        </WidgetBox>
      </WidgetContainer>
    </WidgetTheme>
  )
}
